import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Server, ShieldCheck, Cpu, Barcode, ShoppingBag, Wrench, FileSignature, Network, Monitor, HardDrive } from "lucide-react";
import SEO from "@/components/SEO";
import PartnerLogos from "@/components/PartnerLogos";
import HandDrawnMark from "@/components/HandDrawn";
import { SERVICES } from "@/data/site";

const iconMap = {
  "yazilim-cozumleri": Cpu,
  "pos-sistemleri": Monitor,
  "barkod-sistemleri": Barcode,
  "guvenlik-sistemleri": ShieldCheck,
  "teknik-servis": Wrench,
  "sunucu-kiralama": Server,
  "e-ticaret": ShoppingBag,
  "e-imza-kep": FileSignature,
  "network-kurulum": Network,
};

const accentColor = {
  red: "#FF3B30",
  blue: "#007AFF",
  green: "#34C759",
  black: "#FFFFFF",
};

export default function Home() {
  return (
    <>
      <SEO
        title="Pratik Teknoloji — Uçtan Uca Teknoloji. Sade Çözüm."
        description="Çanakkale'de işletmeler için yazılım, POS, barkod, güvenlik, teknik servis ve sunucu kiralama. Pratik Bilişim Teknolojileri A.Ş."
      />

      {/* HERO — Dark Premium */}
      <section data-testid="home-hero" className="relative bg-[#0A0A0A] text-white overflow-hidden min-h-[96vh] flex items-center">
        <div className="grain absolute inset-0 opacity-40 pointer-events-none"></div>
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-[#FF3B30]/25 rounded-full blur-[160px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#007AFF]/15 rounded-full blur-[140px] pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-24 w-full">
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="flex items-center gap-3 text-xs font-bold tracking-[0.3em] uppercase text-white/80"
          >
            <span className="inline-block h-px w-10 bg-[#FF3B30]"></span>
            <span>
              Sade. Güçlü.{" "}
              <HandDrawnMark className="text-white">Uçtan uca teknoloji</HandDrawnMark>
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08 }}
            className="font-display mt-8 text-[52px] leading-[0.96] md:text-[96px] lg:text-[120px] font-extrabold tracking-[-0.04em] max-w-6xl"
          >
            İşletmenizin<br/>
            <span className="italic font-light text-white/40">dijital</span> omurgası<span className="text-[#FF3B30]">.</span>
          </motion.h1>

          <div className="mt-12 grid md:grid-cols-2 gap-10 items-end">
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.16 }}
              className="text-lg md:text-xl text-white/60 leading-relaxed max-w-xl"
            >
              Yazılım, sunucu, güvenlik ve teknik servis: <span className="text-white font-semibold">sade, güvenli ve tek merkezden yönetilen</span> teknoloji.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.24 }}
              className="flex flex-wrap gap-3 md:justify-end"
            >
              <Link to="/iletisim" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 rounded-full bg-[#FF3B30] text-white px-7 py-4 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors shadow-[0_10px_30px_rgba(255,59,48,0.35)]">
                Ücretsiz Keşif <ArrowRight size={18}/>
              </Link>
              <Link to="/hizmetler" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 text-white px-7 py-4 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors">
                Hizmetleri Keşfet
              </Link>
            </motion.div>
          </div>

          {/* Stat strip */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-4 gap-0 border-t border-white/10 pt-10"
          >
            {[
              { v: "2015", l: "Kuruluş" },
              { v: "7", l: "Yazılım Ortağı" },
              { v: "%99", l: "Sunucu Uptime" },
              { v: "∞", l: "Kesintisiz Destek" },
            ].map((s) => (
              <div key={s.l} className="md:px-8 first:md:pl-0 py-4 md:border-r md:border-white/10 md:last:border-r-0">
                <div className="h-14 md:h-16 flex items-center">
                  <span className={`font-display font-extrabold tracking-tighter text-white leading-none ${s.v === "∞" ? "text-7xl md:text-8xl" : "text-4xl md:text-5xl"}`}>
                    {s.v}
                  </span>
                </div>
                <div className="mt-3 text-[11px] font-bold tracking-[0.25em] uppercase text-white/50">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SERVICES BENTO — Dark */}
      <section data-testid="home-services" className="relative py-20 md:py-32 bg-[#0A0A0A] text-white border-t border-white/10">
        <div className="grain absolute inset-0 opacity-30 pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#FF3B30] mb-3">Neler Yapıyoruz?</div>
              <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight max-w-3xl">
                Dokuz farklı hizmet. <span className="italic font-light text-white/40">Tek merkez.</span>
              </h2>
            </div>
            <Link to="/hizmetler" className="text-sm font-semibold text-white/60 hover:text-white inline-flex items-center gap-2">
              Tüm hizmetler <ArrowUpRight size={16}/>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5 md:[grid-auto-flow:dense]">
            {SERVICES.map((svc) => {
              const Icon = iconMap[svc.slug] || Server;
              const colSpan = svc.featured ? "md:col-span-4 md:row-span-2" : "md:col-span-2";
              const isFeatured = svc.featured;
              const iconColor = accentColor[svc.accent] || "#FFFFFF";

              return (
                <Link
                  to={`/hizmetler/${svc.slug}`}
                  key={svc.slug}
                  data-testid={`service-card-${svc.slug}`}
                  className={`group relative overflow-hidden rounded-3xl border p-6 md:p-8 ${colSpan} min-h-[240px] transition-all duration-300 hover:-translate-y-1 ${
                    isFeatured
                      ? "bg-gradient-to-br from-[#1a1a1a] to-[#0A0A0A] border-white/20 hover:border-[#FF3B30]/60"
                      : "bg-white/[0.03] border-white/10 hover:border-white/30 hover:bg-white/[0.06]"
                  }`}
                >
                  {isFeatured && (
                    <>
                      <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#FF3B30]/20 rounded-full blur-3xl pointer-events-none"></div>
                      {/* Server rack decoration bottom-right */}
                      <div className="absolute right-6 md:right-10 bottom-6 md:bottom-10 pointer-events-none">
                        <div className="relative w-32 md:w-44 h-32 md:h-44">
                          <div className="absolute inset-0 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent"></div>
                          {[0, 1, 2, 3].map((i) => (
                            <div
                              key={i}
                              className="absolute left-3 right-3 h-3 md:h-4 rounded-md bg-white/[0.04] border border-white/10 flex items-center gap-1.5 px-2"
                              style={{ top: `${12 + i * 22}%` }}
                            >
                              <span className="w-1 h-1 rounded-full bg-[#34C759]"></span>
                              <span className="w-1 h-1 rounded-full bg-[#34C759]/60"></span>
                              <span className="ml-auto h-0.5 flex-1 max-w-[60%] rounded bg-white/10"></span>
                            </div>
                          ))}
                          <HardDrive className="absolute -top-4 -right-4 text-[#FF3B30]/60" size={36} strokeWidth={1.4} />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="relative flex flex-col h-full">
                    <div className="flex items-start justify-between mb-6">
                      <div className="text-xs font-bold tracking-[0.3em] text-white/40">{svc.tag}</div>
                      <div
                        className="h-14 w-14 rounded-2xl flex items-center justify-center border group-hover:scale-110 transition-transform"
                        style={{ borderColor: `${iconColor}30`, background: `${iconColor}10`, color: iconColor }}
                      >
                        <Icon size={26} strokeWidth={1.8} />
                      </div>
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white">{svc.title}</h3>
                    <p className={`mt-3 text-sm md:text-base text-white/60 leading-relaxed ${isFeatured ? "max-w-sm" : "max-w-md"}`}>{svc.short}</p>
                    {isFeatured && (
                      <ul className="mt-6 grid grid-cols-1 gap-y-2 text-xs text-white/70 max-w-xs">
                        {svc.bullets.slice(0, 4).map((b) => (
                          <li key={b} className="flex items-start gap-2"><span className="text-[#FF3B30] mt-0.5">→</span><span>{b}</span></li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-auto pt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-white/70 group-hover:text-white group-hover:gap-3 transition-all">
                      Detayları gör <ArrowUpRight size={16}/>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PARTNERS — White contrast section */}
      <section className="relative py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <PartnerLogos />
          <div className="mt-10 text-center">
            <Link to="/is-ortaklari" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A0A0A] hover:text-[#FF3B30]">
              İş ortaklıklarımız hakkında <ArrowUpRight size={16}/>
            </Link>
          </div>
        </div>
      </section>

      {/* BRAND QUOTE — Dark */}
      <section className="relative py-24 md:py-40 bg-[#0A0A0A] text-white overflow-hidden border-t border-white/10">
        <div className="grain absolute inset-0 opacity-30 pointer-events-none"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#FF3B30]/10 rounded-full blur-[180px] pointer-events-none"></div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <blockquote className="font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
            "Kurumsal bir yapıya sahip olsak da, ilk günden beri taşıdığımız <span className="text-[#FF3B30]">amatör ruhu</span> ve çalışma heyecanını hiç kaybetmedik."
          </blockquote>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2">
            <div className="hidden sm:block"></div>
            <div className="flex flex-col items-start">
              <div
                className="text-[#FF3B30] leading-none"
                style={{
                  fontFamily: "'Great Vibes', cursive",
                  fontWeight: 400,
                  fontStyle: "normal",
                  fontSize: "clamp(1.75rem, 2.6vw, 2.25rem)",
                  letterSpacing: "0",
                  transform: "skewX(-8deg) rotate(-6deg)",
                  transformOrigin: "left center"
                }}
              >
                Onur Karpuz
              </div>
              <div className="mt-2 h-[1px] w-24 bg-gradient-to-r from-[#FF3B30]/60 via-[#FF3B30]/30 to-transparent"></div>
              <div className="mt-2 text-[10px] tracking-[0.35em] uppercase font-bold text-white/40">Kurucu</div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-20 md:py-28 bg-[#0A0A0A] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-[#1a1a1a] to-[#0A0A0A] border border-white/10 text-white p-10 md:p-20">
            <div className="grain absolute inset-0 opacity-50 pointer-events-none"></div>
            <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#FF3B30]/30 rounded-full blur-3xl"></div>
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#FF3B30] mb-4">İletişim</div>
                <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.05]">Projenizi konuşalım<span className="text-[#FF3B30]">.</span></h2>
                <p className="mt-5 text-white/60 text-lg max-w-md">Yazılım, sunucu, güvenlik veya teknik servis — bir başlangıç noktanız varsa biz oradan devam ederiz.</p>
              </div>
              <div className="flex flex-wrap gap-4 md:justify-end">
                <Link to="/iletisim" className="inline-flex items-center gap-2 rounded-full bg-[#FF3B30] text-white px-7 py-4 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors">
                  Teklif Al <ArrowRight size={18}/>
                </Link>
                <a href="tel:+902862125655" className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 text-white px-7 py-4 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors">
                  Hemen Ara
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
